// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = authService.getToken();
        if (token) {
          const userData = await authService.getCurrentUser();
          if (userData) {
            setUser(userData);
          } else {
            authService.logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Update user profile
  const updateUserProfile = async (profileData) => {
    try {
      const response = await profileService.updateProfile(profileData);
      if (response.success && response.user) {
        setUser(response.user);
        localStorage.setItem('kapta_user', JSON.stringify(response.user));
        return { success: true, user: response.user };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Register/Create Account
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await authService.register(userData);
      if (result.success) {
        setUser(result.user);
        setSuccess('Account created successfully!');
        return { success: true, data: result };
      } else {
        setError(result.message || 'Registration failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Sign In
  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await authService.login(credentials);
      if (result.success) {
        setUser(result.user);
        setSuccess('Login successful!');
        return { success: true, data: result };
      } else {
        setError(result.message || 'Login failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Google Sign-In / Sign-Up
  const googleLogin = async (credential) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await authService.googleLogin(credential);
      if (result.success) {
        setUser(result.user);
        setSuccess(result.message || 'Signed in with Google!');
        return { success: true, data: result };
      } else {
        setError(result.message || 'Google sign-in failed');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await authService.forgotPassword(email);

      // Now backend returns success: false if user doesn't exist
      if (result.success) {
        // Email was sent successfully
        setSuccess(result.message || 'Password reset link sent to your email');
        return { success: true, data: result };
      } else {
        // User doesn't exist or other error
        setError(result.message || 'No account found with this email');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message || 'Failed to process password reset request');
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
    setSuccess(null);
  };

  const value = {
    user,
    loading,
    error,
    success,
    register,
    login,
    googleLogin, // ✅ Added
    forgotPassword,
    logout,
    updateUserProfile,
    setUser, // ✅ Exposed so GoogleSignInButton can set user directly
    clearMessages: () => {
      setError(null);
      setSuccess(null);
    },
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};